import React, {useState} from "react";
import Output from "../../routes/llm_run/llm_run_detail/components/output";
import TestEditor from "../../routes/llm_run/llm_run_detail/components/test_editor";

import {DataKey} from "common/flowmaster/types/workflow";
import {
  GroupedResultEntry,
  GroupedResults,
  TestId,
} from "common/flowmaster/types/test";
import {EnhancedLlmRun} from "common/flowmaster/types/llm_run";
import {OrganisationId} from "common/types/organisation";

export interface OutputWithTestsProps {
  llmRun: EnhancedLlmRun;
  organisationId: OrganisationId;
  dataKey: DataKey;
  title: string;
  isDeveloperMode?: boolean;
  groupedResults?: GroupedResults<GroupedResultEntry>;
  onAddTest: (dataKey: DataKey, key: string) => void;
  onUpdateTest: (
    testId: TestId,
    testType: string,
    value: Record<string, unknown>,
  ) => void;
  onDeleteTest: (testId: TestId) => void;
  getTestResults: (dataKey: DataKey, key: string) => void;
  onTestEditorOpen?: () => void;
  onTestEditorClose?: () => void;
}

export default function OutputWithTests({
  llmRun,
  dataKey,
  title,
  isDeveloperMode = true,
  groupedResults,
  organisationId,
  onAddTest,
  onUpdateTest,
  onDeleteTest,
  getTestResults,
  onTestEditorOpen,
  onTestEditorClose,
}: OutputWithTestsProps) {
  const [isShowingTestEditor, setIsShowingTestEditor] = useState<
    false | {dataKey: DataKey; key: string; nameKey: string}
  >(false);

  const addTestHandler = async (dataKey: DataKey, key: string) => {
    if (
      !llmRun.tests ||
      llmRun.tests.filter(
        test =>
          test.data_key === dataKey &&
          test.key === key &&
          test.name_key === llmRun.name,
      ).length === 0
    ) {
      await onAddTest(dataKey, key);
    }
    showResults(dataKey, key);
  };

  const showResults = async (dataKey: DataKey, key: string) => {
    getTestResults(dataKey, key);
    setIsShowingTestEditor({dataKey, key, nameKey: llmRun.name});
    onTestEditorOpen?.();
  };

  const handleCloseTestEditor = () => {
    setIsShowingTestEditor(false);
    onTestEditorClose?.();
  };

  return (
    <>
      <Output
        llmRun={llmRun}
        organisationId={organisationId}
        dataKey={dataKey}
        title={title}
        isDeveloperMode={isDeveloperMode}
        groupedResults={groupedResults}
        addTest={addTestHandler}
        showResults={showResults}
      />

      {isShowingTestEditor ? (
        <TestEditor
          llmRunId={llmRun.id}
          organisationId={organisationId}
          state={llmRun.data}
          tests={(llmRun.tests ?? []).filter(
            test =>
              test.data_key === isShowingTestEditor.dataKey &&
              test.key === isShowingTestEditor.key &&
              test.name_key === isShowingTestEditor.nameKey,
          )}
          results={
            llmRun?.test_results?.[isShowingTestEditor.dataKey]?.[
              isShowingTestEditor.key
            ]
          }
          onClose={handleCloseTestEditor}
          onAddTest={() => {
            onAddTest(isShowingTestEditor.dataKey, isShowingTestEditor.key);
          }}
          onUpdateTest={(testId, testType, value) => {
            onUpdateTest(testId, testType, value);
          }}
          onDeleteTest={testId => {
            onDeleteTest(testId);
          }}
        />
      ) : null}
    </>
  );
}
